import { callFunction } from '../utils/apiClient';
import type { LinkUp } from './conversationService';
export type { LinkUp };

/** Which feed a social action targets: the shared public gallery or the user's own private posts. */
export type GalleryScope = 'public' | 'private';

/** One social-media post in an agent's gallery. `image_path` is null on a locked shot until unlocked. */
export interface GalleryPost {
  id: string;
  agent_id: string;
  index: number;
  scene: string;
  aspect: 'square' | 'portrait' | 'story' | string;
  image_path: string | null;
  caption: string;
  locked: boolean;
  like_count: number;
  comment_count: number;
  /** Private posts only — whether the owner liked it. */
  liked?: boolean;
}

export interface GalleryComment { id: string; by: string; name: string; text: string; created_at: string }

export interface AgentGalleryView {
  posts: GalleryPost[];
  following: boolean;
  unlocked: boolean;
  liked: string[];
}

/** The signed-in user's view of an agent's gallery (locked shots hide the image until unlocked). */
export const getAgentGallery = (agentId: string) =>
  callFunction<AgentGalleryView>('getAgentGallery', { agentId });

/** The user's OWN private, credit-funded posts for an agent (always visible — no paywall). */
export const getPrivateGallery = (agentId: string) =>
  callFunction<{ posts: GalleryPost[]; liked: string[]; gen_cost?: number }>('getPrivateGallery', { agentId });

/** Spend credits to generate a fresh, RANDOM private post for an agent (throws "NOT_ENOUGH_CREDITS" when short). */
export const generatePrivatePost = (agentId: string) =>
  callFunction<{ post: GalleryPost }>('generatePrivatePost', { agentId });

/** Toggle a like on a post (a like grows the Link). `scope` selects the public or private feed. */
export const likeGalleryPost = (agentId: string, postId: string, scope: GalleryScope = 'public') =>
  callFunction<{ liked: boolean; like_count: number; linkUp?: LinkUp | null }>('likeGalleryPost', { agentId, postId, scope });

/** Comment on a post — returns the user's comment + the agent's in-character reply. */
export const commentGalleryPost = (agentId: string, postId: string, text: string, scope: GalleryScope = 'public') =>
  callFunction<{ comment: GalleryComment; agentReply?: GalleryComment | null; linkUp?: LinkUp | null }>('commentGalleryPost', { agentId, postId, text, scope });

export const getGalleryComments = (agentId: string, postId: string, scope: GalleryScope = 'public') =>
  callFunction<{ comments: GalleryComment[] }>('getGalleryComments', { agentId, postId, scope }).then((r) => r.comments || []);

/** Follow / unfollow an agent (following grows the Link). */
export const followAgent = (agentId: string, on: boolean) =>
  callFunction<{ following: boolean; linkUp?: LinkUp | null }>('followAgent', { agentId, on });

/** Unlock the agent's full gallery — charges credits (throws "NOT_ENOUGH_CREDITS" when short). */
export const unlockAgentGallery = (agentId: string) =>
  callFunction<{ unlocked: boolean; alreadyUnlocked?: boolean }>('unlockAgentGallery', { agentId });

/** The aspect-ratio box class for a post's shape. */
export const ASPECT_CLASS: Record<string, string> = {
  square: 'aspect-square',
  portrait: 'aspect-[4/5]',
  story: 'aspect-[9/16]',
};
